"use client";
import React, { useEffect, useState } from "react";
import { useUserContext } from "@/context/UserProvider";
import { uniqueAppRedirectionDeeplink } from "@/utils/common.helpers";
import { useRouter } from "next/navigation";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import {
  APP_BANNER_ANIMATE_IN_TIMEOUT,
  APP_DOWNLOAD_SCROLL_BANNER_TIMEOUT,
  APP_DOWNLOAD_SIDE_BANNER_COLLAPSE_TIMEOUT,
  HOME_VIRTUAL_LIST_CONTAINER_ID,
  OrientationEnum,
  EventsEnum,
  EventNamesEnum,
  PageNameEnum,
} from "@/constants/constants";
import { Text } from "./Text";
import { useVideoPlayerContext } from "./videoPlayer/provider/VideoPlayerProvider";
import { CloseIcon } from "./Icons";
import { LogoStack } from "./Logo";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";

interface AppDownloadBannerProps {
  isSideBanner?: boolean;
  isOnWatchPage?: boolean;
  pageName?: PageNameEnum;
  forceShow?: boolean;
}

const enum BannerType {
  SIDE = "side",
  REGULAR = "regular",
}

const AppDownloadBanner = ({
  isSideBanner = false,
  isOnWatchPage = false,
  forceShow = false,
  pageName,
}: AppDownloadBannerProps) => {
  const { hasActiveAccess } = useUserContext();
  const [isVisible, setIsVisible] = useState(false);
  const router = useRouter();
  const { t } = useClientTranslations();
  const { showControls } = useVideoPlayerContext();
  const [isPortrait, setIsPortrait] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const isMobile = typeof window !== "undefined" ? window.isMobile : false;
  const { trackEvent } = useTrackingAnalytics();

  // Check if banner should be shown based on orientation
  // Side banner: mobile and landscape, Regular banner: mobile and portrait
  const shouldForceShowBanner = (forceShow || hasActiveAccess) && isMobile;
  const shouldShowBanner =
    shouldForceShowBanner && (isSideBanner ? isLandscape : isPortrait);

  useEffect(() => {
    router.prefetch(uniqueAppRedirectionDeeplink());
  }, [router]);

  // Check and listen for orientation changes
  useEffect(() => {
    const checkOrientation = () => {
      const portrait = screen.orientation.type.includes(
        OrientationEnum.PORTRAIT,
      );
      const landscape = screen.orientation.type.includes(
        OrientationEnum.LANDSCAPE,
      );
      setIsPortrait(portrait);
      setIsLandscape(landscape);
    };

    // Check initial orientation
    checkOrientation();

    // Listen for orientation changes
    const handleOrientationChange = () => {
      checkOrientation();
    };

    window.addEventListener(
      EventsEnum.ORIENTATION_CHANGE,
      handleOrientationChange,
    );
    screen.orientation?.addEventListener(
      EventsEnum.CHANGE,
      handleOrientationChange,
    );

    return () => {
      window.removeEventListener(
        EventsEnum.ORIENTATION_CHANGE,
        handleOrientationChange,
      );
      screen.orientation?.removeEventListener(
        EventsEnum.CHANGE,
        handleOrientationChange,
      );
    };
  }, []);

  // Control visibility based on showControls and dismissed state (for side banner)
  useEffect(() => {
    if (isSideBanner) {
      if (isDismissed) {
        setIsVisible(false);
        return;
      }
      setIsVisible(shouldShowBanner && !showControls);
    } else if (isOnWatchPage) {
      setIsVisible(shouldShowBanner && !showControls);
    }
  }, [
    showControls,
    isOnWatchPage,
    shouldShowBanner,
    isSideBanner,
    isDismissed,
  ]);

  // Handle collapse/expand animation when side banner becomes visible
  useEffect(() => {
    if (isSideBanner && isVisible) {
      setIsCollapsed(false);

      // Collapse after timeout from when banner becomes visible
      const collapseTimeout = setTimeout(() => {
        setIsCollapsed(true);
      }, APP_DOWNLOAD_SIDE_BANNER_COLLAPSE_TIMEOUT);

      // Cleanup: clear timeout if component unmounts or isVisible changes
      return () => {
        clearTimeout(collapseTimeout);
      };
    } else if (isSideBanner) {
      // Reset to collapsed when banner becomes invisible
      setIsCollapsed(true);
    }
  }, [isVisible, isSideBanner]);

  // Animate in after mount (only for non-watch pages and regular banner)
  useEffect(() => {
    if (isSideBanner || isOnWatchPage || !shouldShowBanner) return;

    const timer = setTimeout(
      () => setIsVisible(true),
      APP_BANNER_ANIMATE_IN_TIMEOUT,
    );
    return () => clearTimeout(timer);
  }, [isOnWatchPage, shouldShowBanner, isSideBanner]);

  // Scroll event listener (only for non-watch pages and regular banner)
  useEffect(() => {
    if (isSideBanner || isOnWatchPage || !shouldShowBanner) return;

    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      setIsVisible(false);

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        setIsVisible(true);
      }, APP_DOWNLOAD_SCROLL_BANNER_TIMEOUT);
    };

    const scrollContainer = document.getElementById(
      HOME_VIRTUAL_LIST_CONTAINER_ID,
    );

    if (!scrollContainer) return;

    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [hasActiveAccess, isOnWatchPage, shouldShowBanner, isSideBanner]);
  if (!shouldShowBanner) {
    return null;
  }
  const redirectionHandler = (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.TouchEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();

    trackEvent({
      eventName: EventNamesEnum.APP_DOWNLOAD_BANNER_CLICKED,
      properties: {
        ...(pageName && { page_name: pageName }),
        banner_type: isSideBanner ? BannerType.SIDE : BannerType.REGULAR,
      },
    });

    router.push(uniqueAppRedirectionDeeplink());
  };

  const handleClose = (event: React.TouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDismissed(true);
    setIsVisible(false);
  };

  const handleBannerTouch = (event: React.TouchEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  // Side banner layout
  if (isSideBanner) {
    return (
      <div
        onTouchEnd={handleBannerTouch}
        className={`flex h-[4.5rem] transform items-center justify-between border-l-2 border-stg-white bg-stg-floating-banner-background px-4 py-2 backdrop-blur-sm transition-all duration-500 ease-in-out ${
          isVisible
            ? "pointer-events-auto translate-x-0 opacity-100"
            : "pointer-events-none translate-x-6 opacity-0"
        }`}
      >
        <LogoStack className="me-2.5 rounded-lg bg-stg-grey-3 p-2" />
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${
            isCollapsed ? "w-0 opacity-0" : "me-10 w-auto opacity-100"
          }`}
        >
          <div className="flex flex-col gap-0.5">
            <Text size={2} weight="medium" classNames="!text-stg-grey-2">
              {t("popups.appDownloadBanner.heading1")}
            </Text>
            <Text size={3} weight="bold" classNames="!text-stg-grey-1">
              {t("popups.appDownloadBanner.heading2")}
            </Text>
            <Text size={1} weight="medium" classNames="!text-stg-grey-2">
              {t("popups.appDownloadBanner.subHeading")}
            </Text>
          </div>
        </div>
        <button
          className="text-nowrap rounded-lg bg-stg-primary-cta p-2"
          onTouchEnd={redirectionHandler}
        >
          <Text size={2} weight="medium">
            {t("popups.appDownloadBanner.cta")}
          </Text>
        </button>
        <button className="ps-2.5" onTouchEnd={handleClose}>
          <CloseIcon width={12} height={12} color="black" />
        </button>
      </div>
    );
  }

  // Regular banner layout
  return (
    <div
      className={`mx-4 flex transform items-center justify-between rounded-lg bg-stg-floating-banner-background px-4 py-3.5 backdrop-blur-sm transition-all duration-500 ease-in-out ${
        isVisible
          ? "pointer-events-auto translate-y-0 opacity-100"
          : "pointer-events-none translate-y-6 opacity-0"
      }`}
      onTouchEnd={handleBannerTouch}
    >
      <LogoStack className="rounded-lg bg-stg-grey-3 p-2" />
      <div className="me-auto ms-2.5 flex flex-col gap-0.5">
        <Text size={2} weight="medium" classNames="!text-stg-grey-2">
          {t("popups.appDownloadBanner.heading1")}
        </Text>
        <Text size={3} weight="bold" classNames="!text-stg-grey-1">
          {t("popups.appDownloadBanner.heading2")}
        </Text>
        <Text size={1} weight="medium" classNames="!text-stg-grey-2">
          {t("popups.appDownloadBanner.subHeading")}
        </Text>
      </div>
      <button
        className="text-nowrap rounded-lg bg-stg-primary-cta p-2"
        onClick={redirectionHandler}
        onTouchEnd={redirectionHandler}
      >
        <Text size={2} weight="medium">
          {t("popups.appDownloadBanner.cta")}
        </Text>
      </button>
    </div>
  );
};

export default AppDownloadBanner;
